import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Search, Heart, User, ShoppingBag, Menu, X } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { useProducts } from "@/context/ProductsContext";

const MEGA_NAMES = [
  ["Lehengas", "lehengas", "/collections/women"],
  ["Sarees", "sarees", "/collections/women"],
  ["Gowns", "gowns", "/collections/women"],
  ["Bridal Couture", "bridal-couture", "/collections/bridal"],
];

export default function Header({ transparent = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [openMobile, setOpenMobile] = useState(false);
  const [openSearch, setOpenSearch] = useState(false);
  const [openMega, setOpenMega] = useState(null);
  const { count: cartCount, setOpen: openCart } = useCart();
  const { count: wishCount } = useWishlist();
  const { user, logout } = useAuth();
  const { settings, nav, loading: siteLoading } = useSite();
  const { categories } = useProducts();
  const navigate = useNavigate();

  const LOGO_BLACK = settings?.logo?.dark;
  const LOGO_WHITE = settings?.logo?.light;
  const megaItems = MEGA_NAMES.map(([name, slug, to]) => ({ name, to, img: categories.find(c => c.slug === slug)?.image || "" }));

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    on(); window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  const solid = !transparent || scrolled;
  const iconClass = solid ? "text-ink" : "text-ivory";

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
          solid ? "bg-ivory/85 backdrop-blur-xl border-b border-border/60" : "bg-transparent"
        }`}
      >
        <div className="w-full flex items-center gap-8 lg:gap-10 py-5 px-6 md:px-8 lg:px-10">
          <button
            data-testid="mobile-menu-btn"
            className={`lg:hidden ${iconClass}`}
            onClick={() => setOpenMobile(true)}
            aria-label="Menu"
          >
            <Menu strokeWidth={1} size={22} />
          </button>

          <Link to="/" data-testid="logo-link" className="flex-shrink-0">
            {siteLoading || !(solid ? LOGO_BLACK : LOGO_WHITE) ? (
              <span className="font-serif text-xl tracking-[0.18em] text-current">BARIQUE</span>
            ) : (
              <img src={solid ? LOGO_BLACK : LOGO_WHITE} alt="BARIQUE" className="h-6 md:h-7 w-auto object-contain transition-opacity duration-500" />
            )}
          </Link>

          <nav className="hidden lg:flex items-center gap-5 xl:gap-6 flex-1 flex-nowrap overflow-x-auto no-scrollbar min-w-0">
            {nav.map((n) => (
              <div
                key={n.id || n.to}
                onMouseEnter={() => n.label === "Collections" && setOpenMega("Collections")}
                onMouseLeave={() => setOpenMega(null)}
                className="flex-shrink-0"
              >
                <NavLink
                  to={n.to}
                  data-testid={`nav-${n.label.replace(/\s+/g, '-').toLowerCase()}`}
                  className={({ isActive }) =>
                    `lux-link whitespace-nowrap ${solid ? "text-ink" : "text-ivory"} ${isActive ? "after:w-full" : ""}`
                  }
                >
                  {n.label}
                </NavLink>
              </div>
            ))}
          </nav>

          <div className={`flex items-center gap-5 flex-shrink-0 ml-auto lg:ml-0 ${iconClass}`}>
            <button data-testid="search-btn" onClick={() => setOpenSearch(true)} aria-label="Search">
              <Search strokeWidth={1} size={20} />
            </button>
            <Link data-testid="wishlist-link" to="/wishlist" className="relative">
              <Heart strokeWidth={1} size={20} />
              {wishCount > 0 && <span className="absolute -top-1 -right-2 text-[10px]">{wishCount}</span>}
            </Link>
            {user ? (
              <Link data-testid="account-link" to="/account"><User strokeWidth={1} size={20} /></Link>
            ) : (
              <Link data-testid="login-link" to="/login"><User strokeWidth={1} size={20} /></Link>
            )}
            <button data-testid="cart-btn" onClick={() => openCart(true)} className="relative">
              <ShoppingBag strokeWidth={1} size={20} />
              {cartCount > 0 && <span className="absolute -top-1 -right-2 text-[10px]" data-testid="cart-count">{cartCount}</span>}
            </button>
          </div>
        </div>

        {/* Mega menu */}
        {openMega && (
          <div
            onMouseEnter={() => setOpenMega(openMega)}
            onMouseLeave={() => setOpenMega(null)}
            className="absolute inset-x-0 top-full bg-ivory border-t border-border animate-reveal"
          >
            <div className="container-lux py-10 grid grid-cols-4 gap-8">
              {megaItems.map((m) => (
                <Link to={m.to} key={m.name} className="group" data-testid={`mega-${m.name.toLowerCase()}`}>
                  <div className="aspect-[3/4] overflow-hidden bg-muted">
                    {m.img ? <img src={m.img} alt={m.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" /> : <div className="w-full h-full bg-muted animate-pulse" />}
                  </div>
                  <p className="mt-3 overline text-ink">{m.name}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </header>

      {/* Mobile drawer */}
      {openMobile && (
        <div className="fixed inset-0 z-[60] bg-ink text-ivory animate-reveal overflow-y-auto">
          <div className="flex items-center justify-between py-5 px-6">
            {LOGO_WHITE ? <img src={LOGO_WHITE} alt="BARIQUE" className="h-5" /> : <span className="font-serif text-lg tracking-[0.18em]">BARIQUE</span>}
            <button data-testid="mobile-close" onClick={() => setOpenMobile(false)} className="text-ivory"><X strokeWidth={1} size={20} /></button>
          </div>
          <nav className="flex flex-col px-6 py-6">
            {nav.map(n => (
              <Link
                key={n.id || n.to}
                to={n.to}
                onClick={() => setOpenMobile(false)}
                className="uppercase tracking-[0.24em] text-[12px] font-medium py-4 border-b border-ivory/10 hover:text-ivory/70 transition-colors"
              >
                {n.label}
              </Link>
            ))}
            <div className="mt-8 flex flex-col gap-4">
              {user ? (
                <>
                  <Link to="/account" onClick={() => setOpenMobile(false)} className="uppercase tracking-[0.24em] text-[11px] font-medium text-ivory/70 hover:text-ivory">Account</Link>
                  <button onClick={() => { logout(); setOpenMobile(false); }} className="uppercase tracking-[0.24em] text-[11px] font-medium text-ivory/70 hover:text-ivory text-left">Logout</button>
                </>
              ) : (
                <Link to="/login" onClick={() => setOpenMobile(false)} className="uppercase tracking-[0.24em] text-[11px] font-medium text-ivory/70 hover:text-ivory">Sign in</Link>
              )}
            </div>
          </nav>
        </div>
      )}

      {/* Search overlay */}
      {openSearch && (
        <div className="fixed inset-0 z-[60] bg-ivory/95 backdrop-blur-xl animate-reveal">
          <div className="container-lux pt-8 flex justify-end">
            <button data-testid="search-close" onClick={() => setOpenSearch(false)}><X strokeWidth={1} /></button>
          </div>
          <div className="container-lux mt-16">
            <p className="overline text-muted-foreground">Search The Maison</p>
            <input
              autoFocus
              data-testid="search-input"
              placeholder="Search couture, silhouettes, occasions…"
              className="w-full mt-4 border-b border-ink bg-transparent py-4 font-serif text-3xl md:text-5xl tracking-tight placeholder:text-muted-foreground/60 focus:outline-none"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  navigate(`/collections?q=${encodeURIComponent(e.currentTarget.value)}`);
                  setOpenSearch(false);
                }
              }}
            />
            <div className="mt-10 grid md:grid-cols-3 gap-4">
              {["Bridal Lehengas", "Ivory Sarees", "Bandhgalas", "Cocktail Gowns", "Kaftans", "Corsets"].map(s => (
                <button
                  key={s}
                  onClick={() => { navigate(`/collections?q=${encodeURIComponent(s)}`); setOpenSearch(false); }}
                  className="text-left overline text-muted-foreground hover:text-ink"
                >{s}</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
